import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchPendingVerifications } from "../../../../redux/slice/companySlice";
import CustomTable from "../../../../components/table/CustomTable";
import { formatMomentDate } from "../../../../utils/dateHelper";
import { CheckmarkCircle04Icon, CalendarIcon } from "../../../../assets/icons";
import { routes } from "../../../../routes/pageRoutes";
import { FileText } from "lucide-react";
import { TableSearch } from "../../../../components/form/Fields";
import StatusChip from "../../../../components/statusChip/StatusChip";

const STATUS_COLORS = {
  approved: "text-success",
  rejected: "text-danger",
  submitted: "text-warning",
  pending: "text-secondary",
  revoked: "text-danger",
};

const getStatusBg = (status) => {
  const lowerStatus = status?.toLowerCase();
  switch (lowerStatus) {
    case "approved":
    case "paid":
      return "success";
    case "submitted":
    case "pending":
      return "blue";
    case "rejected":
      return "warning";
    case "revoked":
      return "danger";
    default:
      return "";
  }
};

const parsePaymentDetails = (details, group) => {
  if (!details) return "-";

  const method = details.payment_method || details;

  if (group === "upi" && method.upi) {
    return `UPI ID: ${method.upi.upi_id || "N/A"}`;
  }
  if (group === "card" && method.card) {
    const { card_bank_name, card_network, card_type, card_number } = method.card;
    const typeFormatted = card_type ? card_type.replace('_', ' ') : '';
    return [card_bank_name, card_network, typeFormatted, `ending in ${card_number}`].filter(Boolean).join(" ").toUpperCase();
  }
  return group ? group.toUpperCase() : "See details";
};

const Verifications = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    pendingVerifications,
    pendingVerificationsLoading,
    pendingVerificationsMeta
  } = useSelector((state) => state.company);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVerification, setSelectedVerification] = useState(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchPendingVerifications({ company_id: id, page, limit, search: searchTerm }));
    }
  }, [dispatch, id, page, limit, searchTerm]);

  useEffect(() => {
    if (pendingVerifications?.length > 0 && !selectedVerification) {
      setSelectedVerification(pendingVerifications[0]);
    }
  }, [pendingVerifications, selectedVerification]);

  const actionList = [
    {
      title: "View",
      handler: (row) => {
        navigate(routes.viewVerificationRequests.replace(":id", row.id));
      }
    }
  ];

  const getLatestPayment = (item) => {
    if (!item.payments || item.payments.length === 0) return null;
    return [...item.payments].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
  };

  const columns = [
    {
      header: 'PAN Number',
      cell: (_, item) => item.pan_number || "-"
    },
    {
      header: 'PAN Status',
      cell: (_, item) => (
        item.pan_status ? (
          <StatusChip bg={getStatusBg(item.pan_status)} pill title={item.pan_status} className="text-capitalize" />
        ) : "-"
      )
    },
    {
      header: 'Business Doc Type',
      cell: (_, item) => item.business_type ? <span className="text-uppercase">{item.business_type}</span> : "-"
    },
    {
      header: 'Business Doc No',
      cell: (_, item) => item.business_number || "-"
    },
    {
      header: 'Business Doc Status',
      cell: (_, item) => (
        item.business_status ? (
          <StatusChip bg={getStatusBg(item.business_status)} pill title={item.business_status} className="text-capitalize" />
        ) : "-"
      )
    },
    {
      header: 'Other Doc Status',
      cell: (_, item) => (
        item.other_documents_status ? (
          <StatusChip bg={getStatusBg(item.other_documents_status)} pill title={item.other_documents_status} className="text-capitalize" />
        ) : "-"
      )
    },
    {
      header: 'Payment Status',
      cell: (_, item) => {
        const payment = getLatestPayment(item);
        const status = payment?.status || item.payment_status;
        return status ? (
          <StatusChip bg={getStatusBg(status)} pill title={status} className="text-capitalize" />
        ) : "-";
      }
    },
    {
      header: 'Payment Source',
      cell: (_, item) => {
        const payment = getLatestPayment(item);
        return payment?.payment_source ? (
          <span className="text-capitalize text-secondary">
            {payment.payment_source.replace('_', ' ')}
          </span>
        ) : "-";
      }
    },
    {
      header: 'Payment Amount',
      cell: (_, item) => {
        const payment = getLatestPayment(item);
        return payment ? <div>{`${payment.amount || 0} ${payment.currency || 'INR'}`}</div> : "-";
      }
    },
    {
      header: 'Payment Method',
      cell: (_, item) => {
        const payment = getLatestPayment(item);
        if (!payment) return "-";

        const method = payment.payment_method;
        if (method && typeof method === 'object') {
          const keys = Object.keys(method);
          if (keys.length > 0) {
            return <span className="text-capitalize">{keys[0]}</span>;
          }
        }
        return typeof method === 'string' ? method : (payment.payment_group || "-");
      }
    },
    {
      header: 'Payment Method Details',
      cell: (_, item) => {
        const payment = getLatestPayment(item);
        if (!payment) return "-";

        const details = payment.payment_details || payment.payment_method;
        if (!details) return "-";

        const group = payment.payment_group ||
          payment.payment_method?.type ||
          (payment.payment_method ? Object.keys(payment.payment_method)[0] : undefined);

        return <div className="text-nowrap small">{parsePaymentDetails(details, group)}</div>;
      }
    },
    {
      header: 'Payment ID',
      cell: (_, item) => {
        const payment = getLatestPayment(item);
        return payment ? <div>{payment.cf_payment_id || payment.cashfree_order_id || "-"}</div> : "-";
      }
    },
    {
      header: 'Paid At',
      cell: (_, item) => {
        const payment = getLatestPayment(item);
        return payment?.paid_at ? formatMomentDate(payment.paid_at, "DD MMM YYYY, hh:mm A") : "-";
      }
    },
    {
      header: 'Overall Status',
      cell: (_, item) => (
        item.overall_status ? (
          <StatusChip bg={getStatusBg(item.overall_status)} pill title={item.overall_status} className="text-capitalize" />
        ) : "-"
      )
    },
    {
      header: 'Verified By',
      cell: (_, item) => {
        if (!item.verifier) return "-";
        const adminName = item.verifier.name || 'Admin';
        const roleName = item.verifier.role?.name || 'N/A';
        const email = item.verifier.email || '';
        return (
          <div className="d-flex flex-column text-start">
            <span className="fw-medium text-success text-capitalize">{adminName} ({roleName})</span>
            <span className="text-muted small">{email}</span>
          </div>
        );
      }
    },
    {
      header: 'Verified At',
      cell: (_, item) => item.verified_at ? formatMomentDate(item.verified_at, "DD MMM YYYY, hh:mm A") : "-"
    },
    {
      header: 'Expires At',
      cell: (_, item) => item.expires_at ? formatMomentDate(item.expires_at, "DD MMM YYYY, hh:mm A") : "-"
    },
    {
      header: 'Revoked By',
      cell: (_, item) => {
        if (item.overall_status === 'revoked' && item.revoked_by) {
          const adminName = item.revoked_by.name || 'Admin';
          const roleName = item.revoked_by.role?.name || 'N/A';
          const email = item.revoked_by.email || '';
          return (
            <div className="d-flex flex-column text-start">
              <span className="fw-medium text-danger text-capitalize">{adminName} ({roleName})</span>
              <span className="text-muted small">{email}</span>
            </div>
          );
        }
        return "-";
      }
    },
    {
      header: 'Revoked At',
      cell: (_, item) => item.overall_status === 'revoked' && item.revoked_at ? formatMomentDate(item.revoked_at, "DD MMM YYYY, hh:mm A") : "-"
    },
    {
      header: 'Created At',
      cell: (_, item) => item.created_at ? formatMomentDate(item.created_at, "DD MMM YYYY, hh:mm A") : "-"
    },
    {
      header: 'Updated At',
      cell: (_, item) => item.updated_at ? formatMomentDate(item.updated_at, "DD MMM YYYY, hh:mm A") : "-"
    }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="verifications-tab grid gap-3">
      {/* List Header */}
      <div className="bg-white rounded-2 overflow-hidden common-shadow g-col-9">
        <div className="p-4 d-flex align-items-center justify-content-between gap-4">
          <TableSearch value={searchTerm}
            placeholder="Search"
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }} />
        </div>
        <div className="overflow-hidden">
          <CustomTable
            columns={columns}
            data={pendingVerifications}
            loading={pendingVerificationsLoading}
            actionList={actionList}
            pagination={{
              currentPage: page,
              pageSize: limit,
              total: pendingVerificationsMeta?.total || pendingVerifications.length || 0,
              totalPages: pendingVerificationsMeta?.pages || Math.ceil((pendingVerificationsMeta?.total || pendingVerifications.length || 0) / limit) || (pendingVerifications.length > 0 ? 1 : 0),
              onPageChange: (newPage) => setPage(newPage),
              setPageSize: (newLimit) => {
                setLimit(newLimit);
                setPage(1);
              },
              setCurrentPage: setPage,
            }}
            onRowClick={(row) => setSelectedVerification(row)}
            noDataMessage="No verification records found."
            tbody={{ trClass: "cursor-pointer" }}
          />
        </div>
      </div>

      {/* Verification Summary (Right Side) */}
      <div className="g-col-3 bg-white rounded-2 common-shadow p-4 align-self-start">
        <div className="d-flex align-items-center gap-2 mb-4">
          <FileText size={20} className="text-primary" />
          <h6 className="mb-0 fw-semibold text-primary">Verification Details</h6>
        </div>

        {selectedVerification ? (
          <div className="d-flex flex-column gap-3">
            <div className="d-flex align-items-center gap-3">
              <span className="p-2 bg-light rounded d-flex align-items-center justify-content-center text-primary" style={{ width: "36px", height: "36px" }}>
                <CheckmarkCircle04Icon size={20} />
              </span>
              <div>
                <small className="text-muted d-block" style={{ fontSize: "11px" }}>Overall Status</small>
                <span className={`fw-semibold text-capitalize ${STATUS_COLORS[selectedVerification.overall_status] || ""}`}>
                  {selectedVerification.overall_status || "-"}
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              <span className="p-2 bg-light rounded d-flex align-items-center justify-content-center text-primary" style={{ width: "36px", height: "36px" }}>
                <CalendarIcon height={20} width={20} />
              </span>
              <div>
                <small className="text-muted d-block" style={{ fontSize: "11px" }}>Payment Status</small>
                <span className={`fw-semibold text-capitalize ${selectedVerification.payment_status === "paid" ? "text-success" : "text-warning"}`}>
                  {selectedVerification.payment_status || "-"}
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              <span className="p-2 bg-light rounded d-flex align-items-center justify-content-center text-primary" style={{ width: "36px", height: "36px" }}>
                <CalendarIcon height={20} width={20} />
              </span>
              <div>
                <small className="text-muted d-block" style={{ fontSize: "11px" }}>Requested On</small>
                <span className="fw-medium text-muted small">
                  {formatDate(selectedVerification.created_at)}
                </span>
              </div>
            </div>

            {selectedVerification.verified_at && (
              <div className="d-flex align-items-center gap-3">
                <span className="p-2 bg-light rounded d-flex align-items-center justify-content-center text-primary" style={{ width: "36px", height: "36px" }}>
                  <CalendarIcon height={20} width={20} />
                </span>
                <div>
                  <small className="text-muted d-block" style={{ fontSize: "11px" }}>Decision Date</small>
                  <span className="fw-medium text-muted small">
                    {formatDate(selectedVerification.verified_at)}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-muted small text-center my-4">Select a verification request to view details</p>
        )}
      </div>
    </div>
  );
};

export default Verifications;
